import { useState } from "react";

import { Alert, Button, OverlayTrigger, Popover } from "react-bootstrap";
import { toast } from "react-toastify";

import { useQueryClient } from "@tanstack/react-query";
import { Column } from "primereact/column";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { TreeNode } from "primereact/treenode";
import { TreeTable } from "primereact/treetable";

import Loader from "@/common/components/Loader";
import { Permissions } from "@/common/enums/sharedEnums.ts";
import { useGetUserType } from "@/modules/auth/hooks/userType.hooks";
import ModalBan from "@/modules/profile/components/ModalBan.tsx";
import history from "@/routes/history";

import ModalUser, { ActionUser } from "../../../components/ModalUser.tsx";
import { useDeleteAgency } from "../../../hooks/agency.hooks.ts";
import {
  useDeleteGroup,
  useGetAllGroups_old,
} from "../../../hooks/group.hooks.ts";
import { useDeleteTeam } from "../../../hooks/team.hooks.ts";
import {
  IManagementAgency,
  IManagementGroup,
  IManagementMember,
  IManagementTeam,
} from "../../../interfaces/management.ts";
import { AGENCY, GROUP, TEAM, USER } from "../../../routes/ProfileRoutes.ts";
import { detachUserTeam } from "../../../services/index.ts";

export enum ManagementType {
  user = "user",
  team = "team",
  agency = "agency",
  group = "group",
}

const ManagementContainer_old = () => {
  const queryClient = useQueryClient();

  const { isSuperAdmin } = useGetUserType();

  const [showModalUser, setShowModalUser] = useState<{
    user: {
      action: ActionUser;
      open: boolean;
      data: any;
    };
  }>({
    user: {
      action: "add",
      open: false,
      data: null,
    },
  });

  const [showModalBan, setShowModalBan] = useState<{
    id: string;
    open: boolean;
    data: any;
  }>({
    id: "",
    open: false,
    data: null,
  });

  const { data, isLoading, isFetching, error } = useGetAllGroups_old();

  const { mutate: deleteGroup, isLoading: isLoadingDeleteGroup } =
    useDeleteGroup({
      handleSuccess: () => toast.success("Le groupe à été supprimé"),
      handleError: () =>
        toast.error(
          "Le groupe n'a pas été supprimé (vérifier qu'il ne contient plus d'agences)"
        ),
    });

  const { mutate: deleteAgency, isLoading: isLoadingDeleteAgency } =
    useDeleteAgency({
      handleSuccess: () => toast.success("L'agence à été supprimée"),
      handleError: () =>
        toast.error(
          "L'agence n'a pas été supprimée (vérifier qu'elle ne contient plus d'équipes)"
        ),
    });

  const { mutate: deleteTeam, isLoading: isLoadingDeleteTeam } = useDeleteTeam({
    handleSuccess: () => toast.success("L'équipe à été supprimée"),
    handleError: () =>
      toast.error(
        "L'équipe n'a pas été supprimée (vérifier qu'elle ne contient plus d'utilisateurs)"
      ),
  });

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Alert variant="danger">
        Impossible de récupérer les informations.&nbsp;Veuillez contacter votre
        administrateur système.
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert variant="danger">
        Erreur de récupération des données : Veuillez réessayer ultérieurement.
      </Alert>
    );
  }

  const deleteAction = (id: string, type: ManagementType) => {
    switch (type) {
      case ManagementType.team:
        deleteTeam(id);
        break;
      case ManagementType.agency:
        deleteAgency(id);
        break;
      case ManagementType.group:
        deleteGroup(id);
        break;
    }
  };

  const deleteConfirm = (id: string, type: ManagementType, label: string) => {
    confirmDialog({
      message: `Voulez vous supprimer ${label} ?`,
      header: "Confirmation de suppression",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      accept: () => deleteAction(id, type),
    });
  };

  const detachUserTeamAction = (userId: string, teamId: string) =>
    detachUserTeam(userId, teamId)
      .then(() => {
        toast.success("L'utilisateur à été détaché");
        queryClient.invalidateQueries(["getAllGroups"]);
      })
      .catch((e) => {
        toast.error("L'utilisateur n'a pas été détaché");
        console.error(e);
      });

  const detachConfirm = (userId: string, teamId: string, label: string) => {
    confirmDialog({
      message: `Voulez vous détacher ${label} ?`,
      header: "Confirmation de détachement",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      accept: () => detachUserTeamAction(userId, teamId),
    });
  };

  const valueTreeTable: TreeNode[] = [
    ...data.groups.map((group: IManagementGroup, indexGroup: number) => ({
      id: group.id,
      key: `${indexGroup}`,
      icon: "pi pi-building",
      data: {
        is_disabled: group.is_disabled,
        reason_disabled: group.reason_disabled,
        level: group.label,
        type: ManagementType.group,
        location: GROUP,
        permissions: {
          show: group.permissions.includes(Permissions.GROUP_GET),
          update: group.permissions.includes(Permissions.GROUP_UPDATE),
          delete: group.permissions.includes(Permissions.GROUP_DELETE),
        },
        hasAgencies:
          Array.isArray(group?.agencies) && group.agencies.length > 0,
        hasInvoiceInfos: group?.agencies?.every(
          (item) =>
            item?.customer?.offerType?.id &&
            item?.customer?.invoiceFrequencyEdl &&
            item?.customer?.invoiceFrequencyErp
        ),
      },
      children: [
        ...(group?.agencies?.map(
          (agency: IManagementAgency, indexAgency: number) => ({
            id: agency.id,
            key: `${indexGroup}-${indexAgency}`,
            icon: "pi pi-home",
            data: {
              is_disabled: agency.is_disabled,
              reason_disabled: agency.reason_disabled,
              level: agency.label,
              isSyncToAgendize: agency.isSyncToAgendize,
              isAuthorizedToSync: agency.isAuthorizedToSync,
              type: ManagementType.agency,
              location: AGENCY,
              parentId: group.id,
              permissions: {
                show: agency.permissions.includes(Permissions.AGENCY_GET),
                update: agency.permissions.includes(Permissions.AGENCY_UPDATE),
                delete: agency.permissions.includes(Permissions.AGENCY_DELETE),
              },
              hasInvoiceInfos: !!(
                agency?.customer?.offerType?.id &&
                agency?.customer?.invoiceFrequencyEdl &&
                agency?.customer?.invoiceFrequencyErp
              ),
            },
            children: [
              ...(agency?.teams?.map(
                (team: IManagementTeam, indexTeam: number) => ({
                  id: team.id,
                  key: `${indexGroup}-${indexAgency}-${indexTeam}`,
                  icon: "pi pi-users",
                  data: {
                    level: team.label,
                    isSyncToAgendize: team.isSyncToAgendize,
                    isAuthorizedToSync: team.isAuthorizedToSync,
                    type: ManagementType.team,
                    role: team.teamRole.role,
                    sector: team.sectors.join(", "),
                    location: TEAM,
                    parentId: agency.id,
                    permissions: {
                      show: team.permissions.includes(Permissions.TEAM_GET),
                      update: team.permissions.includes(
                        Permissions.TEAM_UPDATE
                      ),
                      delete: team.permissions.includes(
                        Permissions.TEAM_DELETE
                      ),
                    },
                  },
                  children: [
                    ...(team?.members?.map(
                      (user: IManagementMember, indexUser: number) => ({
                        id: user.id,
                        key: `${indexGroup}-${indexAgency}-${indexTeam}-${indexUser}`,
                        icon: "pi pi-user",
                        data: {
                          level: `${user.firstname} ${user.lastname}`,
                          isSyncToAgendize: user.isSyncToAgendize,
                          isAuthorizedToSync: user.isAuthorizedToSync,
                          type: ManagementType.user,
                          location: USER,
                          parentId: team.id,
                          permissions: {
                            show: user.permissions.includes(
                              Permissions.USER_GET
                            ),
                            update: user.permissions.includes(
                              Permissions.USER_UPDATE
                            ),
                            delete: user.permissions.includes(
                              Permissions.USER_DELETE
                            ),
                          },
                        },
                      })
                    ) || []),
                    ...(team?.permissions.includes(Permissions.USER_CREATE)
                      ? [
                          {
                            key: `${indexGroup}-${indexAgency}-${indexTeam}-add-button`,
                            data: {
                              level: "Ajouter un utilisateur dans",
                              parent: team.label,
                              type: ManagementType.user,
                              parentId: team.id,
                              isButton: true,
                            },
                          },
                        ]
                      : []),
                  ],
                })
              ) || []),
              ...(agency?.permissions.includes(Permissions.TEAM_CREATE)
                ? [
                    {
                      key: `${indexGroup}-${indexAgency}-add-button`,
                      data: {
                        level: "Ajouter une équipe dans",
                        parent: agency.label,
                        location: TEAM,
                        parentId: agency.id,
                        isButton: true,
                      },
                    },
                  ]
                : []),
            ],
          })
        ) || []),
        ...(group?.permissions.includes(Permissions.AGENCY_CREATE)
          ? [
              {
                key: `${indexGroup}-add-button`,
                data: {
                  level: "Ajouter une agence dans",
                  parent: group.label,
                  location: AGENCY,
                  parentId: group.id,
                  isButton: true,
                },
              },
            ]
          : []),
      ],
    })),
    ...(data.permissions.includes(Permissions.GROUP_CREATE)
      ? [
          {
            key: "add-group-button",
            data: {
              level: "Ajouter un groupe",
              location: GROUP,
              isButton: true,
            },
          },
        ]
      : []),
  ];

  const bodyTemplate = (node: TreeNode) =>
    node?.data?.isButton ? (
      <Button
        variant="light"
        role="button"
        style={{ maxWidth: "370px" }}
        onClick={() => {
          if (node.data.type === ManagementType.user) {
            setShowModalUser({
              ...showModalUser,
              user: { open: true, data: node.data, action: "add" },
            });
          } else {
            if (node.data.location) {
              history.push(node.data.location.replace(":id?", ""), {
                ...(node.data?.parentId && { parentId: node.data.parentId }),
              });
            }
          }
        }}
      >
        <i className="bi bi-plus" />
        {node.data?.level}
        {node.data?.parent && (
          <span className="fw-bold">&nbsp;{node.data.parent}</span>
        )}
      </Button>
    ) : (
      <span>
        {isSuperAdmin &&
          (node?.data?.hasAgencies ||
            node?.data?.type === ManagementType.agency) && (
            <i
              className={`bi ${
                node?.data?.hasInvoiceInfos
                  ? "bi-check-circle-fill text-success"
                  : "bi-exclamation-circle-fill text-danger"
              }
              `}
              style={{
                position: "relative",
                top: 1,
                marginRight: 10,
                fontSize: node?.data?.hasInvoiceInfos ? 20.6 : 20,
              }}
            />
          )}
        <i className={`pi ${node.icon}`} />
        <span className="px-2">{node.data?.level}</span>
        {node.data?.isAuthorizedToSync && (
          <OverlayTrigger
            trigger={["hover", "focus"]}
            placement={"bottom"}
            overlay={
              <Popover id={`actionsComponentLink`}>
                <Popover.Body className="p-0 m-2 popoverTextSecondary">
                  <strong>
                    {node.data?.isSyncToAgendize
                      ? "Est synchronisé avec Agendize"
                      : "N'est pas synchronisé avec Agendize"}
                  </strong>
                </Popover.Body>
              </Popover>
            }
          >
            <i
              className="bi bi-arrow-repeat"
              style={{
                color: `${node.data?.isSyncToAgendize ? "green" : "red"}`,
                fontSize: "20px",
              }}
            ></i>
          </OverlayTrigger>
        )}
      </span>
    );

  const actionTemplate = (node: TreeNode) => {
    if (!node?.id || !node.data?.location) {
      return <></>;
    }

    return (
      <div className="d-flex flex-row gap-2 justify-content-end">
        {node.data.type === ManagementType.group ||
        node.data.type === ManagementType.agency ? (
          <Button
            className="d-flex align-items-center"
            variant={
              node.data.is_disabled ? "outline-danger" : "outline-secondary"
            }
            role="button"
            onClick={() => {
              setShowModalBan({
                id: node.id ?? "",
                open: true,
                data: node.data,
              });
            }}
          >
            <i className="pi pi-ban" />
          </Button>
        ) : null}

        {node?.data?.permissions?.update && (
          <Button
            className="d-flex align-items-center"
            variant="outline-primary"
            role="button"
            onClick={() => {
              if (node.data.type === ManagementType.user) {
                setShowModalUser({
                  ...showModalUser,
                  user: {
                    open: true,
                    data: {
                      ...node.data,
                      id: node.id,
                    },
                    action: "edit",
                  },
                });
              } else {
                history.push(node.data.location.replace(":id?", node.id), {
                  readOnly: false,
                  parentId: node.data.parentId,
                });
              }
            }}
          >
            <i className="pi pi-pencil" />
          </Button>
        )}
        {node?.data?.permissions?.show && (
          <Button
            className="d-flex align-items-center"
            variant="outline-secondary"
            role="button"
            onClick={() => {
              if (node.data.type === ManagementType.user) {
                setShowModalUser({
                  ...showModalUser,
                  user: {
                    open: true,
                    data: {
                      ...node.data,
                      id: node.id,
                    },
                    action: "show",
                  },
                });
              } else {
                history.push(node.data.location.replace(":id?", node.id), {
                  readOnly: true,
                  parentId: node.data.parentId,
                });
              }
            }}
          >
            <i className="pi pi-eye" />
          </Button>
        )}
        {node?.data?.permissions?.delete && (
          <Button
            className="d-flex align-items-center"
            variant="outline-primary"
            role="button"
            onClick={() =>
              node.data.type === ManagementType.user
                ? detachConfirm(node.id!, node.data.parentId!, node.data.level)
                : deleteConfirm(node.id!, node.data.type, node.data.level)
            }
          >
            {node.data.type === ManagementType.user ? (
              <i className="pi pi-times" />
            ) : (
              <i className="pi pi-trash" />
            )}
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <ModalBan
        id={showModalBan.id}
        show={showModalBan.open}
        onHide={() =>
          setShowModalBan({
            id: "",
            open: false,
            data: null,
          })
        }
        data={showModalBan.data}
      />
      {(isLoadingDeleteGroup ||
        isLoadingDeleteAgency ||
        isLoadingDeleteTeam) && <Loader />}
      <ModalUser
        data={showModalUser.user.data}
        show={showModalUser.user.open}
        action={showModalUser.user.action}
        onHide={() =>
          setShowModalUser({
            ...showModalUser,
            user: {
              action: "add",
              open: false,
              data: null,
            },
          })
        }
      />
      <ConfirmDialog />
      <div className="bg-container managementBlock">
        <div className="managementBlock-header">
          <h2 className="mt-1 fw-bold" style={{ fontSize: "24px" }}>
            Gestion
          </h2>
          <div className="managementBlock-header_icons">
            <div className="managementBlock-header_icons-item">
              <i className="pi pi-building" />
              <div>Groupe</div>
            </div>
            <div className="managementBlock-header_icons-item">
              <i className="pi pi-home" />
              <div>Agence</div>
            </div>
            <div className="managementBlock-header_icons-item">
              <i className="pi pi-users" />
              <div>Équipe</div>
            </div>
            <div className="managementBlock-header_icons-item">
              <i className="pi pi-user" />
              <div>utilisateur</div>
            </div>
          </div>
        </div>
        <TreeTable
          tableClassName="table"
          tableStyle={{ borderCollapse: "separate" }}
          value={valueTreeTable}
          resizableColumns
        >
          <Column
            headerStyle={{
              border: "none",
              backgroundColor: "transparent",
            }}
            field="level"
            className="w-100 d-flex align-items-center"
            header="Groupe/Agence/Équipe/Utilisateur"
            body={bodyTemplate}
            expander
          ></Column>
          <Column
            headerStyle={{
              border: "none",
              backgroundColor: "transparent",
              width: "150px",
            }}
            field="role"
            header="Rôle"
          ></Column>
          <Column
            headerStyle={{
              border: "none",
              backgroundColor: "transparent",
            }}
            field="sector"
            header="Secteur"
          ></Column>
          <Column
            headerStyle={{
              border: "none",
              backgroundColor: "transparent",
              width: "200px",
            }}
            body={actionTemplate}
            headerClassName="w-10rem"
          />
        </TreeTable>
      </div>
      {/* {isFetching && (
        <div className="position-fixed top-50 start-50 translate-middle">
          <Loader />
        </div>
      )} */}
    </>
  );
};

export default ManagementContainer_old;
